import {Navigate, useParams, useSearchParams} from 'react-router'
import {validateDateParam} from '@freckle/educator-entities/ts/common/helpers/date-helper'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {Subjects} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {useCourseMemberships} from '@freckle/educator-entities/ts/common/hooks/use-course-memberships'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import {fetchSchoolLicenses} from '@freckle/educator-entities/ts/common/models/license'
import {fetchRlDomains} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {fetchRlStandardSets} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import Notification from '@freckle/school/ts/common/helpers/notification'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {useSchoolAdmin} from '@freckle/school/ts/hooks/use-school-admin'
import {GrowthContainer} from '@freckle/school/ts/pages/growth/components/growth-container'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'

export default function GrowthHandler() {
  const courseMemberships = useCourseMemberships()
  const schools = useSchools()
  const teachers = useTeachersAsASchoolAdmin()
  const schoolAdmin = useSchoolAdmin()

  return (
    <WithResources
      resources={{
        courseMemberships,
        schools,
        teachers,
        schoolAdmin,
      }}
      render={({courseMemberships, schools, teachers, schoolAdmin}) => (
        <Inner
          courseMemberships={courseMemberships}
          schools={schools}
          teachers={teachers}
          schoolAdmin={schoolAdmin}
        />
      )}
    />
  )
}

type InnerPropsT = {
  courseMemberships: CourseMembershipAttrs[]
  schools: SchoolAttrs[]
  teachers: BaseTeacherAttrs[]
  schoolAdmin: SchoolAdminAttrs
}

function Inner(props: InnerPropsT) {
  const {t} = useSafeTranslation()
  const {courseMemberships, schools, teachers, schoolAdmin} = props
  const {pathSchoolId: schoolId} = useParams()
  const [searchParams, _setSearchParams] = useSearchParams()
  const subject = searchParams.get('subject')
  const teacherId = searchParams.get('teacherId')
  const studentId = searchParams.get('studentId')
  const start = searchParams.get('start')
  const end = searchParams.get('end')
  const grade = searchParams.get('grade')
  const domain = searchParams.get('domain')
  const {elaStandardSet} = schoolAdmin
  const schoolIds = schools.map(school => school.id)

  const allSchoolLicenses = useAsync(
    () => fetchSchoolLicenses(schoolIds, true),
    'fetchSchoolLicenses',
    [schoolIds, true],
  )

  const mathDomains = useAsync(
    () => fetchRlDomains('math', schoolAdmin.mathStandardSet, Languages.En),
    'fetchRlDomains',
    ['math', schoolAdmin.mathStandardSet, Languages.En],
  )

  const mathStandardSetId = schoolAdmin.mathStandardSet.id

  const mathStandardSet = useAsync(
    () =>
      fetchRlStandardSets({'content-area': 'math', 'standard-set': mathStandardSetId}).then(
        standardSets => standardSets[0] ?? null,
      ),
    'fetchRlStandardSet',
    ['math', mathStandardSetId],
  )

  const elaDomains = useAsync(
    () =>
      elaStandardSet ? fetchRlDomains('ela', elaStandardSet, Languages.En) : Promise.resolve([]),
    'fetchRlDomains',
    ['ela', elaStandardSet, Languages.En],
  )

  return (
    <WithResources
      resources={{allSchoolLicenses, mathDomains, elaDomains, mathStandardSet}}
      render={({allSchoolLicenses, mathDomains, elaDomains, mathStandardSet}) => {
        const schoolsWithLicenseModels = schools.filter(school => {
          const schoolLicense = allSchoolLicenses.find(license => {
            return license.schoolId === school.id
          })
          return schoolLicense !== undefined
        })

        const firstSchool = schools[0]
        if (firstSchool === undefined) {
          return <Navigate to={Routes.noSchools()} replace />
        }
        if (schoolsWithLicenseModels.length === 0) {
          Notification.displayErrorNotification(
            t('GROWTH_PAGE_NO_SCHOOL_ACTIVE_LICENSE_ERROR_TEXT'),
            3,
          )
          return <Navigate to={Routes.dashboard()} replace />
        }
        if (mathStandardSet === null) {
          return <Navigate to={Routes.dashboard()} replace />
        }
        return (
          <GrowthContainer
            subject={subject !== null ? Subjects.parse(subject) : undefined}
            schools={schoolsWithLicenseModels}
            schoolId={schoolId !== undefined ? parseInt(schoolId, 10) : undefined}
            teachers={teachers}
            courseMemberships={courseMemberships}
            teacherId={teacherId !== null ? parseInt(teacherId, 10) : undefined}
            studentId={studentId !== null ? parseInt(studentId, 10) : undefined}
            start={start !== null ? validateDateParam(start) : undefined}
            end={end !== null ? validateDateParam(end) : undefined}
            grade={grade !== null ? parseInt(grade, 10) : undefined}
            domain={domain}
            mathDomains={mathDomains}
            elaDomains={elaDomains}
            mathStandardSet={mathStandardSet}
            schoolAdmin={schoolAdmin}
          />
        )
      }}
    />
  )
}
